//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import {
  UnisonIconType,
  UnisonColor,
  IUnisonTheme,
} from '@bvengineering/unison-core';
import React, { forwardRef } from 'react';
import styled, { css } from 'styled-components';

import {
  ActionSheetButtonStyles,
  DangerButtonStyles,
  DefaultButtonStyles,
  DefaultIconButtonStyles,
  FlatDefaultButtonStyles,
  FlatDefaultIconButtonStyles,
  FlatPrimaryButtonStyles,
  IButtonStateStyles,
  IButtonStyle,
  InvisibleButtonStyles,
  InvisibleButtonDeprecatedStyles,
  OverlayButtonStyles,
  OverlayIconButtonStyles,
  OverlayIconMediaButtonStyles,
  PrimaryButtonStyles,
  PrimaryLinkButtonStyles,
  SecondaryButtonStyles,
  TertiaryButtonStyles,
} from '@components/atoms/button/buttonStyles';
import Icon from '@components/atoms/icon/Icon';
import Text from '@components/atoms/text/Text';
import { Mixins, TextMixin } from '@theme/mixins';
import { isDefined } from '@utils/filterPredicates';
import { ITrackableComponent, withTracking } from '@utils/withTracking';

export interface IButtonProps extends React.HTMLAttributes<HTMLButtonElement> {
  buttonStyles: IButtonStateStyles;
  form?: string;
  iconColor?: UnisonColor;
  iconName?: UnisonIconType;
  isDisabled?: boolean;
  isDropdown?: boolean;
  leftContent?: React.ReactElement;
  rightContent?: React.ReactElement;
  textMixinOverride?: TextMixin;
  textEllipsizeOverride?: number;
  type?: 'button' | 'reset' | 'submit';
}

interface Props extends IButtonProps, ITrackableComponent {}

// The minimum width for a standard button with a chevron icon (for dropdown menus)
// or any other content on the right side of the button.
const MIN_DROPDOWN_BUTTON_WIDTH = 128;

/**
 * The Button atom renders a button using one of our standard styles
 * pulled from buttonStyles.ts
 *
 * Note: If and when the specs for this component change, such that more customization
 * is needed in the form of extra icons, more text, etc. A "custom" version of this component
 * can be created that supports arbitrary content inside of it. For now, the design covers
 * all spec'd out functionality.
 *
 * @param isDisabled If true, the button is not interactive and renders with the disabled styles
 * @param iconName If provided, will render an icon in the button to the left of any text
 * @param isDropdown If true, renders a special-case style that include a dropdown icon on the right
 *                   side of the button and pushes the button's content to the left.
 * @param leftContent Allows the developer to embed complex content to the left of the button text
 * @param rightContent Similar to isDropdown, but allows the developer to embed complex content. By
 *                     default, the NestedButton component should be used.
 *
 * @example
 * <Button buttonStyles={ DefaultButtonStyles }>Today</Button>
 * <Button iconName='calendar' buttonStyles={ DefaultIconButtonStyles } />
 * <Button isDropdown={ true } buttonStyles={ DefaultButtonStyles }>Feed Posts</Button>
 * <Button isDropdown={ true } buttonStyles={ DefaultButtonStyles }>Week</Button>
 */
export const Button = forwardRef<HTMLButtonElement, Props>(
  (
    {
      buttonStyles,
      children,
      className,
      iconColor,
      iconName,
      isDisabled,
      isDropdown,
      leftContent,
      onClick,
      rightContent,
      textMixinOverride,
      textEllipsizeOverride,
      trackingId,
      type,
      ...buttonProps
    },
    ref,
  ) => {
    // The icon, rendered to the left of any text
    const icon = iconName ? (
      <IconWrapper>
        <StyledIcon name={iconName} iconColor={iconColor} />
      </IconWrapper>
    ) : null;

    // The text, rendered in the center of the button
    const text = children ? (
      <ChildWrapper>
        <StyledText
          wordBreak='break-all'
          mixin={textMixinOverride ?? Mixins.Text.Body}
          offset={buttonStyles.isInlineWithText ? 0 : -0.05} // -0.05 visually centers the text in all buttons
          ellipsize={textEllipsizeOverride ?? 1}
        >
          {children}
        </StyledText>
      </ChildWrapper>
    ) : null;

    return (
      <StyledButtonWrapper
        buttonStyles={buttonStyles}
        className={className}
        isDisabled={isDisabled}
      >
        {leftContent && <NestedInvisibleDiv>{leftContent}</NestedInvisibleDiv>}
        <NestedInvisibleButton
          {...buttonProps}
          hasSibling={!!rightContent || !!leftContent}
          buttonStyles={buttonStyles}
          onClick={onClick}
          disabled={isDisabled}
          isDropdown={isDropdown}
          {...(isDefined(type) && { type })}
          trackingId={trackingId}
          ref={ref}
        >
          <ChildrenWrapper>
            {icon}
            {text}
          </ChildrenWrapper>
          {isDropdown && (
            <NestedChevron>
              <StyledIcon name='chevron-down' />
            </NestedChevron>
          )}
        </NestedInvisibleButton>
        {rightContent && (
          <NestedInvisibleDiv>{rightContent}</NestedInvisibleDiv>
        )}
      </StyledButtonWrapper>
    );
  },
);

Button.displayName = 'Button';

// True if the footprint of the button will be a square
const isSquare = (borderShape: IButtonStateStyles['fixed']['borderShape']) =>
  ['circle', 'square'].includes(borderShape);

const hasRoundedBorders = (
  borderShape: IButtonStateStyles['fixed']['borderShape'],
) => ['pill', 'circle'].includes(borderShape);

const getButtonStateStyle = (
  stateStyle: IButtonStyle,
  fixedStyle: IButtonStateStyles['fixed'],
) => `
  background-color: ${stateStyle.backgroundColor};
  box-shadow: inset 0 0 0 ${
    fixedStyle.borderThickness === 'thick' ? '1.5px' : '1px'
  } ${stateStyle.borderColor};
  ${StyledText}, ${StyledIcon} {
    color: ${stateStyle.color};
  }
  text-decoration: ${stateStyle.textDecoration ?? 'none'};
  text-decoration-color: ${stateStyle.color};
`;

export const getButtonStyle = ({
  theme,
  buttonStyles: { fixed, standard, hover, active, disabled, isInlineWithText },
  isDropdown,
  rightContent,
  isDisabled,
}: {
  theme: IUnisonTheme;
  buttonStyles: IButtonStateStyles;
  isDropdown?: boolean;
  rightContent?: React.ReactElement;
  isDisabled?: boolean;
}) => css`
  /* Fixed styles */
  display: inline-flex;
  overflow: hidden;
  cursor: pointer;
  text-align: left;
  outline: none;
  border: 0;
  height: ${isInlineWithText ? theme.spacings[4] : theme.spacings[5]};
  min-width: ${isDropdown || rightContent
    ? `${MIN_DROPDOWN_BUTTON_WIDTH}px`
    : theme.spacings[5]};
  border-radius: ${hasRoundedBorders(fixed.borderShape)
    ? theme.borderRadii.pill
    : theme.borderRadii[2]};
  justify-content: center;
  ${StyledText}, ${StyledIcon} {
    font-weight: ${fixed.fontWeight === 'normal'
      ? theme.fontWeights.normal
      : theme.fontWeights.semiBold};
  }
  /* Standard styles */
  ${getButtonStateStyle(standard, fixed)};
  /* Hover styles */
  &:hover {
    ${getButtonStateStyle(hover, fixed)};
  }
  /* Active styles */
  &:active {
    ${getButtonStateStyle(active, fixed)};
  }
  /* Disabled styles */
  ${isDisabled
    ? `
  ${getButtonStateStyle(disabled, fixed)};
  pointer-events: none;
  cursor: default;
`
    : ``}
`;

const StyledButtonWrapper = withTracking(
  styled.div<Props>(
    ({ theme, buttonStyles, isDropdown, rightContent, isDisabled }) =>
      getButtonStyle({
        theme,
        buttonStyles,
        isDropdown,
        rightContent,
        isDisabled,
      }),
  ),
);

type NestedButtonProps = Props & { hasSibling: boolean };

const NestedInvisibleButton = withTracking(
  styled.button<NestedButtonProps>(
    ({ theme, hasSibling, buttonStyles: { fixed }, isDropdown }) => css`
      font-family: inherit;
      cursor: pointer;
      ${fixed.borderShape === 'none'
        ? 'padding: 0px;'
        : `padding: ${theme.spacings[2]} ${
            isSquare(fixed.borderShape) ? theme.spacings[2] : theme.spacings[3]
          };`}
      background: none;
      display: flex;
      align-items: center;
      justify-content: ${isDropdown ? 'space-between' : 'center'};
      ${isDropdown && `gap: ${theme.spacings[1]}`};
      border: none;
      outline: none;
      flex: 1;
      ${hasSibling &&
      `
        justify-content: flex-start;
        padding-right: 0;
      `};
      ${isDropdown && `padding-right: ${theme.spacings[2]};`};
    `,
  ),
);

const NestedInvisibleDiv = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 1 auto;
  padding: 0;
`;

const NestedChevron = styled.span(
  () => `
  background: none;
  height: 100%;
  border: none;
  display: flex;
  align-items: center;
  outline: none;
`,
);

const ChildrenWrapper = styled.div(
  ({ theme }) => `
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  & > :not(:first-child) {
    margin-left: ${theme.spacings[1]};
  }
`,
);

const ChildWrapper = styled.div`
  display: inline-block;
`;

const IconWrapper = styled(ChildWrapper)(
  ({ theme }) => `
  display: inline-flex;
  align-items: center;
  height: ${theme.spacings[3]};
`,
);

export const StyledIcon = styled(Icon)<{
  iconColor?: UnisonColor;
}>(
  ({ iconColor }) => css`
    ${iconColor && `color: ${iconColor} !important;`}
  `,
);

const StyledText = styled(Text)`
  width: 100%;
`;

type PrestyledProps = Omit<Props, 'buttonStyles'>;

export const PrimaryButton = (props: PrestyledProps) => <Button buttonStyles={PrimaryButtonStyles} {...props} />;

export const SecondaryButton = (props: PrestyledProps) => <Button buttonStyles={SecondaryButtonStyles} {...props} />;

export const TertiaryButton = (props: PrestyledProps) => <Button buttonStyles={TertiaryButtonStyles} {...props} />;

export const DefaultButton = (props: PrestyledProps) => <Button buttonStyles={DefaultButtonStyles} {...props} />;

export const FlatPrimaryButton = (props: PrestyledProps) => <Button buttonStyles={FlatPrimaryButtonStyles} {...props} />;

export const FlatDefaultButton = (props: PrestyledProps) => <Button buttonStyles={FlatDefaultButtonStyles} {...props} />;

export const DangerButton = (props: PrestyledProps) => <Button buttonStyles={DangerButtonStyles} {...props} />;

export const DefaultIconButton = (props: PrestyledProps) => <Button buttonStyles={DefaultIconButtonStyles} {...props} />;

export const FlatDefaultIconButton = (props: PrestyledProps) => <Button buttonStyles={FlatDefaultIconButtonStyles} {...props} />;

export const OverlayButton = (props: PrestyledProps) => <Button buttonStyles={OverlayButtonStyles} {...props} />;

export const OverlayIconButton = forwardRef<HTMLButtonElement, PrestyledProps>(
  (props, ref) => (
    <Button buttonStyles={OverlayIconButtonStyles} {...props} ref={ref} />
  ),
);
OverlayIconButton.displayName = 'OverlayIconButton';

export const OverlayIconMediaButton = (props: PrestyledProps) => <Button buttonStyles={OverlayIconMediaButtonStyles} {...props} />;

export const PrimaryLinkButton = (props: PrestyledProps) => <Button buttonStyles={PrimaryLinkButtonStyles} {...props} />;

export const InvisibleButton = (props: PrestyledProps) => <Button buttonStyles={InvisibleButtonStyles} {...props} />;

export const InvisibleButtonDeprecated = (props: PrestyledProps) => (<Button buttonStyles={InvisibleButtonDeprecatedStyles} {...props} />);

export const ActionSheetButton = (props: PrestyledProps) => <Button buttonStyles={ActionSheetButtonStyles} {...props} />;

export default Button;
